import { Button, Grid, Stack, TextField } from "@mui/material";
import React, { Fragment, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import SingleBiometric from "../display/SingleBiometric";

export default function Biometrics() {
  const [showError, setShowError] = React.useState(false);
  const [biometricState, setBiometricState] = React.useState(() => {
    // Load the state from localStorage on initial render
    const savedState = localStorage.getItem("biometricState");
    return savedState ? JSON.parse(savedState) : null;
  });

  const methods = useFormContext();
  const { t } = useTranslation();

  useEffect(() => {
    // Sync biometricState with react-hook-form state (biometrics)
    if (biometricState) {
      methods.setValue("biometrics", biometricState);
    }
  }, [biometricState, methods]);

  function appendBiometric() {
    const biometrics = {
      temperature: methods.getValues("temperature") || null,
      heartRate: methods.getValues("heartRate") || null,
      weight: methods.getValues("weight") || null,
      breathingFrequency: methods.getValues("breathingFrequency") || null,
      length: methods.getValues("length") || null,
      height: methods.getValues("height") || null,
    };

    if (isEmpty(biometrics)) {
      setShowError(true);
    } else {
      setShowError(false);
      setBiometricState(biometrics);
      methods.setValue("biometrics", biometrics);
      localStorage.setItem("biometricState", JSON.stringify(biometrics)); // Save to localStorage
    }
  }

  function removeBiometric() {
    setBiometricState(null);
    setShowError(false);
    methods.setValue("biometrics", null);
    localStorage.removeItem("biometricState"); // Remove from localStorage
  }

  function isEmpty(obj) {
    return Object.values(obj).every((x) => x === null || x === "");
  }

  return (
    <Fragment>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Controller
            name="temperature"
            defaultValue={""}
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t("BodyTemperature")}
                fullWidth
                inputProps={{ maxLength: 20 }}
                data-testid="appointment.finish-meeting.biometrics.temperature-input"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name="heartRate"
            defaultValue={""}
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t("Puls/Min")}
                fullWidth
                inputProps={{ maxLength: 20 }}
                data-testid="appointment.finish-meeting.biometrics.heart-rate-input"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name="weight"
            defaultValue={""}
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t("Mass")}
                fullWidth
                inputProps={{ maxLength: 20 }}
                data-testid="appointment.finish-meeting.biometrics.weight-input"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name="breathingFrequency"
            defaultValue={""}
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t("FrequencyOfBreathing")}
                fullWidth
                inputProps={{ maxLength: 20 }}
                data-testid="appointment.finish-meeting.biometrics.breathing-frequency-input"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name="length"
            defaultValue={""}
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t("length")}
                fullWidth
                inputProps={{ maxLength: 20 }}
                data-testid="appointment.finish-meeting.biometrics.length-input"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name="height"
            defaultValue={""}
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t("height")}
                fullWidth
                inputProps={{ maxLength: 20 }}
                data-testid="appointment.finish-meeting.biometrics.height-input"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Stack
            direction={"row"}
            spacing={2}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              size="small"
              onClick={() => appendBiometric()}
              variant="contained"
              sx={{ width: 150 }}
              data-testid="appointment.finish-meeting.biometrics.add-button"
            >
              {t("Add")}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={() => removeBiometric()}
              variant="contained"
              sx={{ width: 150 }}
              data-testid="appointment.finish-meeting.biometrics.remove-button"
            >
              {t("Remove")}
            </Button>
          </Stack>
        </Grid>
      </Grid>
      {showError ? (
        <SimpleAlert
          message={"PleaseInputBiometricsData"}
          severity={"warning"}
          testId="appointment.finish-meeting.biometrics.error-alert"
        />
      ) : null}
      {biometricState ? (
        <SingleBiometric biometric={biometricState} index={"Data"} />
      ) : null}
    </Fragment>
  );
}
